import Alert from '@/components/Alert/Alert'
import TableView, { TableSection } from '@/components/TableView'
import {
  destroyFormCooperatorThunk,
  ShallowFormCooperator,
  updateFormCooperatorThunk,
} from '@/redux/slices/formCooperatorSlice'
import { ShallowFormRole } from '@/redux/slices/formRoleSlice'
import { useAppDispatch } from '@/redux/store'
import { isFulfilled } from '@reduxjs/toolkit'
import { useCallback, useMemo } from 'react'

type Props = {
  cooperator: ShallowFormCooperator
  roles: ShallowFormRole[]
  formToken: string
  isFormOwner?: boolean
  currentUserId: string
  onRoleChangeSuccess?: (isCurrentUser: boolean) => void
  onRemoveSuccess?: (isCurrentUser: boolean) => void
}

export default function FormCooperatorDetail({
  cooperator,
  roles,
  formToken,
  isFormOwner = false,
  currentUserId,
  onRoleChangeSuccess,
  onRemoveSuccess,
}: Props) {
  const dispatch = useAppDispatch()

  const currentRoleId = cooperator.formRole?.id

  const isCurrentUser = currentUserId === cooperator.id

  const handleRolePress = useCallback(
    async (roleId: string) => {
      if (roleId === currentRoleId) return

      if (isCurrentUser) {
        Alert.alert('变更权限', '变更自己的权限后，将无法管理该表单，确认变更吗？', [
          { text: '取消', style: 'cancel' },
          {
            text: '变更',
            style: 'destructive',
            onPress: async () => {
              if (
                isFulfilled(await dispatch(updateFormCooperatorThunk({ formToken, roleId, targetId: cooperator.id })))
              ) {
                onRoleChangeSuccess?.(isCurrentUser)
              }
            },
          },
        ])
        return
      }

      if (isFulfilled(await dispatch(updateFormCooperatorThunk({ formToken, roleId, targetId: cooperator.id })))) {
        onRoleChangeSuccess?.(isCurrentUser)
      }
    },
    [currentRoleId, dispatch, formToken, cooperator.id, isCurrentUser, onRoleChangeSuccess]
  )

  const handleRemovePress = useCallback(() => {
    Alert.alert(
      isCurrentUser ? '离开协作' : '移除协作者',
      isCurrentUser ? '确认离开该表单的协作吗？' : `确认移除协作者 ${cooperator.displayName} 吗？`,
      [
        {
          text: '取消',
          style: 'cancel',
        },
        {
          text: isCurrentUser ? '离开' : '移除',
          style: 'destructive',
          onPress: async () => {
            await dispatch(destroyFormCooperatorThunk({ formToken, targetId: cooperator.id }))
            onRemoveSuccess?.(isCurrentUser)
          },
        },
      ]
    )
  }, [cooperator.displayName, cooperator.id, dispatch, formToken, onRemoveSuccess, isCurrentUser])

  const sections = useMemo<TableSection[]>(() => {
    const groups: TableSection[] = []

    // Section 1: User info
    groups.push({
      title: '',
      rows: [
        {
          title: cooperator.displayName,
          subTitle: cooperator.email ?? undefined,
          icon: cooperator.avatarUrl ?? undefined,
        },
      ],
    })

    // Section 2: Roles (radio selection for cooperators, static for owner)
    if (isFormOwner) {
      groups.push({
        title: '权限',
        rows: [
          {
            title: '表单创建者',
          },
        ],
      })
    } else {
      groups.push({
        title: '权限',
        rows: roles.map(role => ({
          title: role.name,
          type: 'radio' as const,
          radioSelected: role.id === currentRoleId,
          onPress: () => handleRolePress(role.id),
        })),
      })

      // Section 3: Remove action (only for cooperators, not for owner)
      groups.push({
        title: '',
        rows: [
          {
            title: isCurrentUser ? '离开' : '移除协作',
            type: 'action' as const,
            actionType: 'destructive' as const,
            onPress: handleRemovePress,
          },
        ],
      })
    }

    return groups
  }, [cooperator, roles, currentRoleId, isFormOwner, isCurrentUser, handleRolePress, handleRemovePress])

  return <TableView sections={sections} tableViewStyle="insetGrouped" />
}
